// client/src/hooks/useFirstImportDetection.ts
//
// Detecta si una importación es la PRIMERA del user (en cualquier device).
//
// Strategy: B con fallback A
//   1. Primero chequea localStorage flag — si dice "ya importé antes", no es primera.
//      Esto es instantáneo, sin network call.
//   2. Si no hay flag, hace query a Supabase: ¿este user ya tiene sessions?
//      Esto cubre el caso de "user con multiple devices" — puede ser su primera
//      importación EN ESTE DEVICE pero no la primera global.
//
// La función `markImportComplete()` debe llamarse después de una importación
// exitosa para que la próxima vez no detecte como primera.

import { useCallback, useState } from 'react';
import { User } from '@supabase/supabase-js';
import { supabase, SUPABASE_ENABLED } from '../lib/supabase';

const LOCAL_STORAGE_KEY = 'grindlog_first_import_done';

export interface FirstImportDetectionState {
  /**
   * Determina si la importación que está por suceder es la primera del user.
   * Llamar ANTES de procesar los archivos. Devuelve true si es primera importación.
   */
  checkIsFirstImport: () => Promise<boolean>;
  /**
   * Marcar como "ya importó alguna vez". Llamar DESPUÉS de una importación exitosa.
   * Setea el flag local para evitar futuros checks.
   */
  markImportComplete: () => void;
  /** Loading mientras se hace el check. */
  checking: boolean;
}

export function useFirstImportDetection(user: User | null): FirstImportDetectionState {
  const [checking, setChecking] = useState(false);

  const checkIsFirstImport = useCallback(async (): Promise<boolean> => {
    // Si no hay user, asumimos que NO es primera importación (no podemos verificar)
    if (!user || !SUPABASE_ENABLED) return false;

    setChecking(true);

    try {
      // Step 1: Check localStorage flag (fast path)
      try {
        const flag = localStorage.getItem(LOCAL_STORAGE_KEY);
        if (flag === 'true') {
          // Already imported before on this device — not first
          return false;
        }
      } catch {
        // localStorage may be unavailable (private mode, etc.) — fall through to server check
      }

      // Step 2: Check Supabase (slow path, but covers cross-device)
      const { data, error } = await supabase
        .from('sessions')
        .select('id')
        .eq('user_id', user.id)
        .limit(1);

      if (error) {
        console.warn('[useFirstImportDetection] sessions check failed:', error.message);
        // En caso de error, conservadoramente NO mostramos el modal — mejor no
        // molestar que mostrar falso modal a un user que ya importó.
        return false;
      }

      const hasExistingSessions = Array.isArray(data) && data.length > 0;

      if (hasExistingSessions) {
        // User has previous sessions on another device. Set local flag so we
        // don't query again on next import.
        try {
          localStorage.setItem(LOCAL_STORAGE_KEY, 'true');
        } catch {
          // ignore
        }
        return false;
      }

      // No previous sessions found anywhere → genuinely first import
      return true;
    } finally {
      setChecking(false);
    }
  }, [user]);

  const markImportComplete = useCallback(() => {
    try {
      localStorage.setItem(LOCAL_STORAGE_KEY, 'true');
    } catch {
      // ignore — flag is non-critical
    }
  }, []);

  return { checkIsFirstImport, markImportComplete, checking };
}
